import { ApplicationRef, ComponentRef, EnvironmentInjector, Injectable, createComponent, inject, signal } from '@angular/core';
import { ActiveToast } from './toast.model';
import { ToastContainerComponent } from './toast-container.component';

/**
 * Type → css-class. Compatibel met het `toastrConfig.iconClasses`-gebruik uit ngx-toastr
 * (o.a. in `app.component.ts`), zodat dat call-site ongewijzigd blijft werken.
 */
const ICON_CLASSES = {
	error: 'toast-error',
	info: 'toast-info',
	success: 'toast-success',
	warning: 'toast-warning',
} as const;

/** Zichtbaarheidsduur (ms) vóór auto-dismiss — gelijk aan de ngx-toastr default. */
const TIMEOUT = 5000;
/** Verlengde duur (ms) nadat de muis de toast heeft verlaten — gelijk aan ngx-toastr default. */
const EXTENDED_TIMEOUT = 1000;
/** Duur van de uit-animatie (ms); moet overeenkomen met de CSS-animatie in de component-SCSS. */
const LEAVE_DURATION = 300;

/**
 * Lichte, eigen vervanger voor ngx-toastr's `ToastrService`.
 *
 * Behoudt bewust dezelfde publieke API die in de codebase gebruikt wordt
 * (`success` / `error` / `warning` / `info` / `show` + `toastrConfig.iconClasses`),
 * zodat bestaande call-sites enkel hun import-pad hoeven te wijzigen.
 *
 * De container-component wordt lazy één keer aangemaakt en aan `document.body`
 * gehangen; alle positionering en styling komen uit de component-SCSS.
 */
@Injectable({ providedIn: 'root' })
export class ToastrService {
	/** Compat-shim voor `this.toastr.toastrConfig.iconClasses[type]`. */
	readonly toastrConfig = { iconClasses: ICON_CLASSES };

	/** Actieve toasts, nieuwste bovenaan. Gelezen door de container-component. */
	readonly toasts = signal<readonly ActiveToast[]>([]);

	private readonly appRef = inject(ApplicationRef);
	private readonly environmentInjector = inject(EnvironmentInjector);

	private containerRef?: ComponentRef<ToastContainerComponent>;
	private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();
	private counter = 0;

	success(message = ''): number {
		return this.add(message, ICON_CLASSES.success);
	}

	error(message = ''): number {
		return this.add(message, ICON_CLASSES.error);
	}

	warning(message = ''): number {
		return this.add(message, ICON_CLASSES.warning);
	}

	info(message = ''): number {
		return this.add(message, ICON_CLASSES.info);
	}

	/** Toont een toast met een expliciete css-class, zoals gebruikt in `app.component.ts`. */
	show(message = '', cssClass = ''): number {
		return this.add(message, cssClass);
	}

	/** Start de uit-animatie en verwijdert de toast na afloop uit de lijst. */
	remove(id: number): void {
		const toast = this.toasts().find((t) => t.id === id);
		if (!toast || toast.leaving) return;

		this.clearTimer(id);
		this.toasts.update((list) => list.map((t) => (t.id === id ? { ...t, leaving: true } : t)));

		setTimeout(() => this.toasts.update((list) => list.filter((t) => t.id !== id)), LEAVE_DURATION);
	}

	/** Pauzeert de auto-dismiss zolang de muis boven de toast hangt. */
	pause(id: number): void {
		const handle = this.timers.get(id);
		if (handle !== undefined) clearTimeout(handle);
	}

	/** Hervat de auto-dismiss met een verlengde duur nadat de muis weg is. */
	resume(id: number): void {
		if (this.timers.has(id)) this.arm(id, EXTENDED_TIMEOUT);
	}

	private add(message: string, cssClass: string): number {
		this.ensureContainer();

		const id = ++this.counter;
		const toast: ActiveToast = { id, message: message ?? '', cssClass, closeButton: true, leaving: false };
		this.toasts.update((list) => [toast, ...list]);
		this.arm(id, TIMEOUT);

		return id;
	}

	private arm(id: number, duration: number): void {
		const existing = this.timers.get(id);
		if (existing !== undefined) clearTimeout(existing);

		this.timers.set(
			id,
			setTimeout(() => this.remove(id), duration)
		);
	}

	private clearTimer(id: number): void {
		const handle = this.timers.get(id);
		if (handle !== undefined) clearTimeout(handle);

		this.timers.delete(id);
	}

	private ensureContainer(): void {
		if (this.containerRef) return;

		const ref = createComponent(ToastContainerComponent, { environmentInjector: this.environmentInjector });
		this.appRef.attachView(ref.hostView);
		document.body.appendChild(ref.location.nativeElement);
		this.containerRef = ref;
	}
}
